import { IconWarningYellow } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Portal } from '@chakra-ui/react/portal';
import { Text, TextProps } from '@chakra-ui/react/text';
import { Tooltip } from '@chakra-ui/react/tooltip';

import Dot from '@/components/Dot/Dot';
import { IconTooltip } from '@/components/IconTooltip/IconTooltip';
import { MachineType } from '@/models/MachineType';
import { Stack } from '@/models/Stack';

const MachineAndStack = ({
  machineType,
  stack,
  stackRollbackVersion,
  textStyle = 'body/lg/regular',
  color = 'text/body',
  flexDir = 'column',
}: {
  textStyle?: TextProps['textStyle'];
  color?: TextProps['color'];
  flexDir?: 'row' | 'column';
  machineType?: MachineType;
  stack: Stack;
  stackRollbackVersion?: string;
}) => (
  // Bitkit registers `tooltip` as a slot recipe, so these primitives arrive already wearing the design
  // system's tooltip with no props. BitkitTooltip is this same composition; its `text: string` cannot
  // carry the second line the design asks for here.
  <Tooltip.Root
    disabled={!machineType?.specs}
    positioning={{ placement: 'top', offset: { mainAxis: 4, crossAxis: 0 } }}
  >
    <Tooltip.Trigger asChild>
      <Box
        display="flex"
        flexDir={flexDir}
        alignItems={flexDir === 'row' ? 'center' : 'flex-start'}
        flexWrap="wrap"
        gap="2"
      >
        <Box display="flex" gap={4} alignItems="baseline">
          <Text textStyle={textStyle} color={color}>
            {stack.displayName}
          </Text>
          {stackRollbackVersion && (
            <IconTooltip
              icon={IconWarningYellow}
              position="relative"
              text="Builds using a rollback stack version start slower."
              top={3}
            />
          )}
        </Box>
        {machineType && (
          <>
            {flexDir === 'row' && <Dot size="4" backgroundColor="icon/tertiary" marginInline="8" />}
            <Text textStyle={textStyle} color={color}>
              {machineType.displayName}
            </Text>
          </>
        )}
      </Box>
    </Tooltip.Trigger>
    <Portal>
      <Tooltip.Positioner>
        <Tooltip.Content>
          <Tooltip.Arrow>
            <Tooltip.ArrowTip />
          </Tooltip.Arrow>
          Stack &amp; machine
          <Text>{machineType?.specs}</Text>
        </Tooltip.Content>
      </Tooltip.Positioner>
    </Portal>
  </Tooltip.Root>
);

export default MachineAndStack;
